import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'

import { useIntl } from 'react-intl'

import { capitalize } from '@mui/material'

import { formatLocation } from 'app/features/ListingFeature/helpers/listing.helpers'
import { useGetUserBySlug } from 'app/services/Query/user/user.query'
import useAuthStore, {
  getAuthState,
  getAuthToken,
  getAuthUserSlug
} from 'app/store/auth.store'
import { USER_ROLE } from 'app/utils/constants/role.constants'
import {
  REVIEWS_VARIANT,
  STORE_TABS
} from 'app/utils/constants/status.constants'
import { getLastUrlSegment } from 'app/utils/helpers/functions.helpers'
import { hasStatusError } from 'app/utils/helpers/type.helpers'

import { seoDescriptionMessages } from '../lib/seoDescriptionMessages'
import { getSeoImageUrl } from '../lib/storeLayout.helpers'

export const useStoreLayout = () => {
  const { formatMessage } = useIntl()
  const { query, pathname, isFallback, replace } = useRouter()
  const slug = query?.slug as string
  const lastSegmentPath = getLastUrlSegment(pathname) || ''

  const authUserSlug = useAuthStore(getAuthUserSlug)
  const token = useAuthStore(getAuthToken)
  const { initialized } = useAuthStore(getAuthState)

  const enabled = !!slug && !!authUserSlug

  const { data: user } = useGetUserBySlug({
    slug,
    options: {
      enabled,
      onError: (error) => {
        if (hasStatusError(error)) {
          if (error.status === 404 || error.status === 406) {
            if (enabled) replace('/404')
          }
        }
      }
    }
  })

  const userHasProducts = !!user?.available_listings
  const isProductsPage = lastSegmentPath === STORE_TABS.products
  const hasProductsPage = !userHasProducts && isProductsPage

  const hideReportBlock = isProductsPage || slug === authUserSlug
  const contactName = user?.contact_name || ''
  const userName = user?.store_name || contactName
  const isBusinessUser = user?.role === USER_ROLE.BUSINESS

  const title = `${userName} ${capitalize(lastSegmentPath)}`
  const descriptionKey = isProductsPage
    ? STORE_TABS.products
    : STORE_TABS.reviews
  const description = formatMessage(seoDescriptionMessages[descriptionKey], {
    sellerName: userName
  })

  const seoImageUrl = useMemo(
    () => getSeoImageUrl(user?.avatar?.url),
    [user?.avatar?.url]
  )

  const userLocation =
    formatLocation({ addressObj: user, withoutZipCode: !isBusinessUser }) || ''

  useEffect(() => {
    if (hasProductsPage && !!user?.id && !!slug) {
      const url = '/store/[slug]/reviews'

      const isSellerReviews = user?.is_seller_reviews
      const receive = isSellerReviews
        ? REVIEWS_VARIANT.seller
        : REVIEWS_VARIANT.buyer

      replace({ pathname: url, query: { slug, receive } })
    }
  }, [hasProductsPage, user?.id])

  useEffect(() => {
    if (isFallback) return
    if (enabled) return
    if (!!token && !initialized) return

    if (!!slug && !authUserSlug && !user?.available_listings) {
      replace('/404')
    }
  }, [isFallback, initialized, token])

  return {
    slug,
    title,
    isBusinessUser,
    lastSegmentPath,
    user,
    userName,
    isFallback,
    userLocation,
    userHasProducts,
    hideReportBlock,
    description,
    seoImageUrl
  }
}
